<template lang="pug">
v-row(no-gutters, justify="end")
  v-col(cols="12", sm="10", offset-sm="2")
    ReactionCountFull.my-1.my-1(
      :likes-count="likesCount",
      :ideas-count="ideasCount",
      :cheers-count="cheersCount",
      :memberReactions="memberReactions"
      @reaction-clicked="handleReactionClick"
    )
    .my-2.d-inline-flex.align-start
      div(:style="{ lineHeight: '20px', minHeight: '20px' }")
        v-icon(color="textColor", :size="18", v-if="postsSummary") {{ mdiCommentAccountOutline }}
      SamePageLink.pl-1(
        v-if="postsSummary",
        type="hyperlink",
        :label="postsSummary",
        :small="true",
        target="#suus__post-list",
        color="textColor"
      )
    .my-4.d-flex.flex-column.flex-sm-row
      .my-2.my-sm-0.mr-sm-2
        ReactionComboButton(
          v-if="canReact",
          :selected-reaction="userReaction",
          @create-reaction="handleCreateReaction",
          @delete-reaction="handleDeleteReaction",
          @update-reaction="handleUpdateReaction"
        )
      ScrollTo(targetId="suus__post-form", :inline="false", v-if="canPost")
        Button.my-2.my-sm-0(:block="true")
          v-icon(left) {{ mdiMessagePlusOutline }}
          | Antworten
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref, ComputedRef } from "vue"
import { Emoji, Topic } from "suus-api"
import { ReactionCounts } from "../../interfaces/reactionCounts.interface"
import useReaction from "../../concerns/useReaction"

import ReactionCountFull from "../ReactionCount/ReactionCountFull.vue"
import ReactionComboButton from "../ReactionComboButton/ReactionComboButton.vue"
import SamePageLink from "../UI/SamePageLink.vue"
import Button from "../UI/Button.vue"
import ScrollTo from "../UI/ScrollTo.vue"
import { mdiCommentAccountOutline } from "@mdi/js"
import { mdiMessagePlusOutline } from "@mdi/js"
import { ViewContext } from "concerns/useContextName"
import { MemberReaction } from "components/ReactionCount/reaction-count.types"

export default defineComponent({
  name: "TopicFooter",
  components: {
    ReactionCountFull,
    ReactionComboButton,
    SamePageLink,
    Button,
    ScrollTo,
  },
  props: {
    topic: { type: Object as PropType<Topic>, required: true },
    postsCount: { type: Number, default: 0 },
    canReact: { type: Boolean, default: true },
    canPost: { type: Boolean, default: true },
    viewContext: { type: Object as PropType<ViewContext> },
  },
  setup(props, context) {
    const initialReactionCounts: Ref<ReactionCounts> = computed(() => {
      return {
        likesCount: props.topic.likes_count,
        ideasCount: props.topic.ideas_count,
        cheersCount: props.topic.cheers_count,
      }
    })
    const intitialUserReaction: Ref<Emoji | undefined> = computed(
      () => props.topic.own_reaction?.emoji
    )

    const {
      createReaction,
      deleteReaction,
      updateReaction,
      likesCount,
      ideasCount,
      cheersCount,
      userReaction,
      reactions,
    } = useReaction(
      { id: props.topic.id, type: "Topic" },
      initialReactionCounts,
      intitialUserReaction
    )

    const memberReactions: ComputedRef<MemberReaction[]> = computed(() => {
      return reactions.value.map((reaction) =>
        MemberReaction.fromReaction(reaction, props.viewContext)
      )
    })

    async function handleDeleteReaction(prevReaction) {
      await deleteReaction(props.topic.id.toString(), prevReaction)
    }

    async function handleCreateReaction(newReaction) {
      await createReaction(props.topic.id.toString(), newReaction)
    }

    async function handleUpdateReaction({ prevReaction, newReaction }) {
      await updateReaction(props.topic.id.toString(), newReaction, prevReaction)
    }

    async function handleReactionClick(reaction: Emoji) {
      const topicId = props.topic.id.toString()
      const currentReaction = userReaction.value

      if (currentReaction === reaction) {
        await deleteReaction(topicId, reaction)
      } else if (currentReaction) {
        await updateReaction(topicId, reaction, currentReaction)
      } else {
        await createReaction(topicId, reaction)
      }
    }

    function composePostsSummary(count: number): string {
      const isOne = count === 1
      let amountText = `${count} Antworten`
      if (isOne) amountText = "1 Antwort"

      return amountText
    }

    const postsSummary = computed(() => {
      return props.postsCount ? composePostsSummary(props.postsCount) : ""
    })

    return {
      likesCount,
      ideasCount,
      cheersCount,
      userReaction,
      handleDeleteReaction,
      handleCreateReaction,
      handleUpdateReaction,
      handleReactionClick,
      postsSummary,
      mdiCommentAccountOutline,
      mdiMessagePlusOutline,
      memberReactions,
    }
  },
})
</script>
